import React, { useState } from 'react';
import CalcShell from '../CalcShell';

const LENGTH_UNITS = ['meters','kilometers','centimeters','millimeters','micrometers','miles','yards','feet','inches'];
const ANGLE_UNITS = ['degree °', 'radian'];

function UnitSelect({ value, onChange, opts = LENGTH_UNITS, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
      {opts.map(u => <option key={u} value={u}>{u}</option>)}
    </select>
  );
}
function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`}
        className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#3d621d] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`}
        className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}
function InputRow({ label, value, onChange, unit, onUnitChange, testid, unitTestid, unitOpts }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_150px] items-center gap-2 mb-2">
      <label className="text-sm text-slate-700">{label}</label>
      <input type="number" step="any" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="px-3 py-2 rounded border border-slate-300 bg-white w-full" />
      {onUnitChange && <UnitSelect value={unit} onChange={onUnitChange} testid={unitTestid} opts={unitOpts} />}
    </div>
  );
}
function ResultBlock({ show, lines, finalValue, finalUnit }) {
  if (!show) return null;
  return (
    <div className="mt-3">
      <div className="bg-[#4c7b25] text-white px-4 py-1.5 text-lg font-bold rounded-t">Result</div>
      <div className="bg-white border border-slate-200 rounded-b p-4 font-serif">
        <div className="text-slate-800 text-[15px] leading-8">
          {lines.map((ln, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="min-w-[70px]">{i === 0 ? 'Area =' : '='}</span>
              <span dangerouslySetInnerHTML={{ __html: ln }} />
            </div>
          ))}
          <div className="flex items-start gap-2 mt-1">
            <span className="min-w-[70px]">=</span>
            <span className="font-bold text-[#4c7b25] text-lg">{finalValue} <span className="text-base">{finalUnit}<sup>2</sup></span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
function ShapeCard({ title, children }) {
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4 items-start">
        <div className="bg-slate-100 rounded-md p-4">{children[0]}</div>
        <div className="flex items-center justify-center p-3">{children[1]}</div>
      </div>
      {children[2]}
    </section>
  );
}

const G = '#4a7c1f';
const dashed = { stroke: G, strokeDasharray: '3 3', fill: 'none' };
const solid = { stroke: '#0f172a', fill: 'none', strokeWidth: 1.5 };
const lbl = { fill: G, fontSize: 12, fontStyle: 'italic', fontFamily: 'serif' };

function RectDiagram() {
  return (
    <svg viewBox="0 0 220 160" className="w-full max-w-[220px]">
      <rect x="30" y="40" width="160" height="90" {...solid} />
      <text x="105" y="150" style={lbl}>l</text>
      <text x="200" y="90" style={lbl}>w</text>
    </svg>
  );
}
function TriDiagram() {
  return (
    <svg viewBox="0 0 220 180" className="w-full max-w-[220px]">
      <path d="M110,20 L200,150 L20,150 Z" {...solid} />
      <text x="55" y="90" style={lbl}>a</text>
      <text x="165" y="90" style={lbl}>b</text>
      <text x="105" y="165" style={lbl}>c</text>
    </svg>
  );
}
function TrapDiagram() {
  return (
    <svg viewBox="0 0 220 180" className="w-full max-w-[220px]">
      <path d="M70,30 L150,30 L200,150 L20,150 Z" {...solid} />
      <line x1="110" y1="30" x2="110" y2="150" {...dashed} />
      <text x="105" y="25" style={lbl}>a</text>
      <text x="105" y="165" style={lbl}>b</text>
      <text x="115" y="95" style={lbl}>h</text>
    </svg>
  );
}
function CircleDiagram() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <circle cx="100" cy="100" r="75" {...solid} />
      <line x1="100" y1="100" x2="175" y2="100" {...dashed} />
      <circle cx="100" cy="100" r="2" fill="#0f172a" />
      <text x="135" y="95" style={lbl}>r</text>
    </svg>
  );
}
function SectorDiagram() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <path d="M100,100 L100,20 A80,80 0 0 1 175,120 Z" {...solid} />
      <path d="M100,100 A25,25 0 0 1 125,105" fill="none" stroke="#2563eb" strokeWidth="1.5" />
      <text x="120" y="95" style={{ ...lbl, fill: '#2563eb' }}>angle</text>
      <text x="145" y="115" style={{ ...lbl, transform: 'rotate(35deg)', transformOrigin: '145px 115px' }}>radius</text>
    </svg>
  );
}
function EllipseDiagram() {
  return (
    <svg viewBox="0 0 220 180" className="w-full max-w-[220px]">
      <ellipse cx="110" cy="90" rx="90" ry="55" {...solid} />
      <line x1="110" y1="90" x2="200" y2="90" {...dashed} />
      <line x1="110" y1="90" x2="110" y2="35" {...dashed} />
      <circle cx="110" cy="90" r="2" fill="#0f172a" />
      <text x="155" y="85" style={lbl}>a</text>
      <text x="115" y="60" style={lbl}>b</text>
    </svg>
  );
}
function ParallelogramDiagram() {
  return (
    <svg viewBox="0 0 220 160" className="w-full max-w-[220px]">
      <path d="M40,120 L160,120 L200,40 L80,40 Z" {...solid} />
      <line x1="80" y1="40" x2="80" y2="120" {...dashed} />
      <text x="100" y="140" style={lbl}>b</text>
      <text x="55" y="85" style={lbl}>h</text>
    </svg>
  );
}

/* Sections */
function RectSection() {
  const [l, setL] = useState(''), [w, setW] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const L = parseFloat(l), W = parseFloat(w); if (!L || !W) return; setRes({ v: L * W, L, W }); };
  return (
    <ShapeCard title="Rectangle">
      <div>
        <InputRow label="Length (l)" value={l} onChange={setL} unit={unit} onUnitChange={setUnit} testid="rect-l" unitTestid="rect-l-unit" />
        <InputRow label="Width (w)" value={w} onChange={setW} unit={unit} onUnitChange={setUnit} testid="rect-w" unitTestid="rect-w-unit" />
        <CalcButtons testId="rect-area" onCalc={calc} onClear={() => { setL(''); setW(''); setRes(null); }} />
      </div>
      <RectDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toString()} lines={res ? [`l × w`, `${res.L} × ${res.W}`] : []} />
    </ShapeCard>
  );
}
function TriSection() {
  const [a, setA] = useState(''), [b, setB] = useState(''), [c, setC] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => {
    const A = parseFloat(a), B = parseFloat(b), C = parseFloat(c);
    if (!A || !B || !C) return;
    const s = (A + B + C) / 2;
    const v = Math.sqrt(s * (s - A) * (s - B) * (s - C));
    if (!isFinite(v)) return;
    setRes({ v, A, B, C, s });
  };
  return (
    <ShapeCard title="Triangle">
      <div>
        <InputRow label="Edge 1 (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="tri-a" unitTestid="tri-a-unit" />
        <InputRow label="Edge 2 (b)" value={b} onChange={setB} unit={unit} onUnitChange={setUnit} testid="tri-b" unitTestid="tri-b-unit" />
        <InputRow label="Edge 3 (c)" value={c} onChange={setC} unit={unit} onUnitChange={setUnit} testid="tri-c" unitTestid="tri-c-unit" />
        <CalcButtons testId="tri-area" onCalc={calc} onClear={() => { setA(''); setB(''); setC(''); setRes(null); }} />
      </div>
      <TriDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`√[s(s−a)(s−b)(s−c)] (Heron), s = (a+b+c)/2 = ${res.s}`, `√[${res.s}×${(res.s - res.A).toFixed(2)}×${(res.s - res.B).toFixed(2)}×${(res.s - res.C).toFixed(2)}]`] : []} />
    </ShapeCard>
  );
}
function TrapSection() {
  const [a, setA] = useState(''), [b, setB] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const A = parseFloat(a), B = parseFloat(b), H = parseFloat(h); if (!A || !B || !H) return; setRes({ v: 0.5 * (A + B) * H, A, B, H }); };
  return (
    <ShapeCard title="Trapezoid">
      <div>
        <InputRow label="Base 1 (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="trap-a" unitTestid="trap-a-unit" />
        <InputRow label="Base 2 (b)" value={b} onChange={setB} unit={unit} onUnitChange={setUnit} testid="trap-b" unitTestid="trap-b-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="trap-h" unitTestid="trap-h-unit" />
        <CalcButtons testId="trap-area" onCalc={calc} onClear={() => { setA(''); setB(''); setH(''); setRes(null); }} />
      </div>
      <TrapDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toString()} lines={res ? [`½(a + b) × h`, `½ × (${res.A} + ${res.B}) × ${res.H}`] : []} />
    </ShapeCard>
  );
}
function CircleSection() {
  const [r, setR] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const R = parseFloat(r); if (!R) return; setRes({ v: Math.PI * R * R, R }); };
  return (
    <ShapeCard title="Circle">
      <div>
        <InputRow label="Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cir-r" unitTestid="cir-unit" />
        <CalcButtons testId="cir-area" onCalc={calc} onClear={() => { setR(''); setRes(null); }} />
      </div>
      <CircleDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`πr<sup>2</sup>`, `π × ${res.R}<sup>2</sup>`] : []} />
    </ShapeCard>
  );
}
function SectorSection() {
  const [r, setR] = useState(''), [ang, setAng] = useState(''), [unit, setUnit] = useState('meters'), [aUnit, setAUnit] = useState('degree °');
  const [res, setRes] = useState(null);
  const calc = () => {
    const R = parseFloat(r), A = parseFloat(ang);
    if (!R || !A) return;
    const rad = aUnit === 'degree °' ? A * Math.PI / 180 : A;
    setRes({ v: 0.5 * R * R * rad, R, A, rad });
  };
  return (
    <ShapeCard title="Sector">
      <div>
        <InputRow label="Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="sec-r" unitTestid="sec-r-unit" />
        <InputRow label="Angle (A)" value={ang} onChange={setAng} unit={aUnit} onUnitChange={setAUnit} unitOpts={ANGLE_UNITS} testid="sec-ang" unitTestid="sec-ang-unit" />
        <CalcButtons testId="sec-area" onCalc={calc} onClear={() => { setR(''); setAng(''); setRes(null); }} />
      </div>
      <SectorDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`½ × r<sup>2</sup> × θ<sub>rad</sub>`, `½ × ${res.R}<sup>2</sup> × ${res.rad.toFixed(6)}`] : []} />
    </ShapeCard>
  );
}
function EllipseSection() {
  const [a, setA] = useState(''), [b, setB] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const A = parseFloat(a), B = parseFloat(b); if (!A || !B) return; setRes({ v: Math.PI * A * B, A, B }); };
  return (
    <ShapeCard title="Ellipse">
      <div>
        <InputRow label="Axis 1 (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="ell-a" unitTestid="ell-a-unit" />
        <InputRow label="Axis 2 (b)" value={b} onChange={setB} unit={unit} onUnitChange={setUnit} testid="ell-b" unitTestid="ell-b-unit" />
        <CalcButtons testId="ell-area" onCalc={calc} onClear={() => { setA(''); setB(''); setRes(null); }} />
      </div>
      <EllipseDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`π × a × b`, `π × ${res.A} × ${res.B}`] : []} />
    </ShapeCard>
  );
}
function ParaSection() {
  const [b, setB] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters');
  const [res, setRes] = useState(null);
  const calc = () => { const B = parseFloat(b), H = parseFloat(h); if (!B || !H) return; setRes({ v: B * H, B, H }); };
  return (
    <ShapeCard title="Parallelogram">
      <div>
        <InputRow label="Base (b)" value={b} onChange={setB} unit={unit} onUnitChange={setUnit} testid="par-b" unitTestid="par-b-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="par-h" unitTestid="par-h-unit" />
        <CalcButtons testId="par-area" onCalc={calc} onClear={() => { setB(''); setH(''); setRes(null); }} />
      </div>
      <ParallelogramDiagram />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toString()} lines={res ? [`b × h`, `${res.B} × ${res.H}`] : []} />
    </ShapeCard>
  );
}

export default function AreaCalc() {
  return (
    <CalcShell look="cnet"
      title="Area Calculator"
      subtitle='Below are area calculators for several common shapes. Please fill in the corresponding fields and click "Calculate".'
    >
      <RectSection />
      <TriSection />
      <TrapSection />
      <CircleSection />
      <SectorSection />
      <EllipseSection />
      <ParaSection />
    </CalcShell>
  );
}
